import React, { useState, useEffect } from 'react';
import { Package, ShoppingBag, FolderTree, Users, Clock, Send, Settings, Filter } from 'lucide-react';
import client from '../../api/client';
import { formatTimeAgo } from '../../utils/date';
import { ORDER_STATUS_LABELS } from '../../constants/statuses';

const ENTITY_ICONS = {
  product: { Icon: Package, color: 'bg-[#E8A838]/15 text-[#E8A838]', label: 'Товары' },
  order: { Icon: ShoppingBag, color: 'bg-[#4A6FA5]/15 text-[#4A6FA5]', label: 'Заказы' },
  category: { Icon: FolderTree, color: 'bg-[#6B9B74]/15 text-[#6B9B74]', label: 'Категории' },
  team_member: { Icon: Users, color: 'bg-[#0D9488]/15 text-[#0D9488]', label: 'Команда' },
  broadcast: { Icon: Send, color: 'bg-[#8B5CF6]/15 text-[#8B5CF6]', label: 'Рассылки' },
  setting: { Icon: Settings, color: 'bg-[#64748b]/15 text-[#64748b]', label: 'Настройки' },
};

const ACTION_LABELS = {
  created: 'Создан',
  updated: 'Изменён',
  deleted: 'Удалён',
  status_changed: 'Смена статуса',
  sent: 'Отправлен',
};

const SETTING_LABELS = {
  bot_username: 'Username бота',
  store_name: 'Название магазина',
  store_logo: 'Логотип магазина',
  store_subtitle: 'Подзаголовок магазина',
  timezone_offset: 'Часовой пояс',
  subscription_price: 'Цена подписки за месяц',
  discount_3m: 'Скидка 3 месяца',
  discount_6m: 'Скидка 6 месяцев',
  discount_9m: 'Скидка 9 месяцев',
  discount_12m: 'Скидка 12 месяцев',
};

export function settingLabel(key) {
  return SETTING_LABELS[key] || key;
}

const STATUS_LABELS = ORDER_STATUS_LABELS;

// Убираем HTML-теги из названия сущности для отображения в логе
const stripHtml = (str) => str ? str.replace(/<[^>]+>/g, '') : '';

export function getDetailsText(entity_type, action, details) {
  if (!details) return '';

  if (entity_type === 'setting') {
    if (details.old === undefined && details.new === undefined) return '';
    const fmt = (v) => (v === undefined || v === null || v === 'None' || v === 'null' ? '—' : v);
    return `${fmt(details.old)} → ${fmt(details.new)}`;
  }

  if (action === 'status_changed') {
    const oldLabel = STATUS_LABELS[details.old] || details.old;
    const newLabel = STATUS_LABELS[details.new] || details.new;
    return `${oldLabel} → ${newLabel}`;
  }

  if (action === 'created') {
    if (entity_type === 'product') return `Цена: ${details.price} ₽, кол-во: ${details.quantity}`;
    if (entity_type === 'order') return `Сумма: ${details.total} ₽`;
    if (entity_type === 'broadcast') return details.scheduled ? 'Запланирована' : 'Отправлена сразу';
    return '';
  }

  if (action === 'sent') {
    if (entity_type === 'broadcast') return `Доставлено: ${details.sent_count} из ${details.recipients_count}`;
    return '';
  }

  if (action === 'updated' && typeof details === 'object') {
    const keys = Object.keys(details);
    if (keys.length === 0) return '';
    const first = keys[0];
    const change = details[first];
    if (change?.old !== undefined && change?.new !== undefined) {
      return `${first}: ${change.old} → ${change.new}`;
    }
    return keys.join(', ');
  }

  return '';
}

export default function ActivityLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    setLoadError('');
    client.get('/activity-log/recent?limit=200')
      .then(res => setLogs(res.data || []))
      .catch(() => setLoadError('Не удалось загрузить журнал изменений'))
      .finally(() => setLoading(false));
  }, []);

  const filteredLogs = filter === 'all' ? logs : logs.filter(log => log.entity_type === filter);
  const entityTypes = [...new Set(logs.map(log => log.entity_type))];

  return (
    <div className="animate-fade-in">
      {loadError && (
        <div className="mb-4 rounded-xl border border-[#D64545]/30 bg-[#D64545]/10 p-4 text-sm text-[#D64545]">
          {loadError}
        </div>
      )}
      {/* Фильтр */}
      <div className="mb-4 flex flex-wrap gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`rounded-xl px-3 py-1.5 text-xs font-medium transition ${
            filter === 'all'
              ? 'bg-[#374151] text-white'
              : 'bg-white text-[#64748b] ring-1 ring-[#e2e8f0] hover:bg-[#f1f5f9]'
          }`}
        >
          Все
        </button>
        {entityTypes.map(type => {
          const entity = ENTITY_ICONS[type];
          if (!entity) return null;
          const { Icon, label } = entity;
          return (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium transition ${
                filter === type
                  ? 'bg-[#374151] text-white'
                  : 'bg-white text-[#64748b] ring-1 ring-[#e2e8f0] hover:bg-[#f1f5f9]'
              }`}
            >
              <Icon className="h-3 w-3" />
              {label}
            </button>
          );
        })}
      </div>

      <div className="py-2">
        {loading ? (
          <div className="rounded-2xl bg-white py-16 text-center text-sm text-[#64748b]">
            Загрузка...
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="rounded-2xl bg-white py-16 text-center text-sm text-[#64748b]">
            <Clock className="mx-auto mb-2 h-10 w-10 text-[#e2e8f0]" />
            <p>{filter === 'all' ? 'Пока нет изменений' : 'Нет записей этого типа'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 [&>*]:min-w-0">
            {filteredLogs.map(log => {
              const entity = ENTITY_ICONS[log.entity_type] || ENTITY_ICONS.product;
              const { Icon } = entity;
              const actionLabel = ACTION_LABELS[log.action] || log.action;
              const detailsText = getDetailsText(log.entity_type, log.action, log.details);
              const entityName = log.entity_type === 'setting'
                ? settingLabel(log.entity_name)
                : stripHtml(log.entity_name);

              return (
                <div key={log.id} className="flex items-start gap-3 rounded-xl bg-white p-3 ring-1 ring-[#e2e8f0]">
                  <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${entity.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[#0f172a]">{entityName}</span>
                      <span className="text-xs text-[#64748b]">{actionLabel}</span>
                    </div>
                    {detailsText && (
                      <div className="mt-0.5 text-xs text-[#64748b]">{detailsText}</div>
                    )}
                  </div>
                  <div className="shrink-0 text-xs text-[#64748b]">
                    {formatTimeAgo(log.created_at)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
